import React, { useContext, useEffect, useState } from 'react'
import {
  Button,
  IconButton,
  Modal,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Inline,
  Container,
  Spinner,
} from '@airbus/components-react/components'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleExclamation, faExternalLinkAlt, faSquareCheck } from '@fortawesome/free-solid-svg-icons'
import ModalSn from 'components/Modal/modalSn'
import axiosUtils from 'utils/api/axiosUtils'
import axios from 'utils/api/axios'
import urlApis from 'utils/api/urlApis'
import { CustomDatePicker as DatePicker } from 'components/FormFields/FormDate'
import { format } from 'date-fns'
import PageHeader from 'components/pageHeader'
import { AxiosError } from 'axios'
import { RequiredTool, RequiredToolInput } from 'types/workorder/requiredTool'
import { Lang } from 'context/LangContext'
import { PN_TYPES } from 'constants/constants'

import LightDarkContext from 'context/LightDarkModeContext'
import { CustomInput } from 'components/FormFields/FormInput'
import ResponsiveTableCell from 'components/responsiveTableCell'

type Props = {
  open: boolean
  onClose: () => void
  onSubmit: () => void
  resources: {
    [resourceType: string]: RequiredTool[]
  }
  setSelectedResource: (sr: RequiredToolInput) => void
  lang: Lang
  oenumdoc: string
}

type CopyState = {
  checked?: boolean
  open?: boolean
  loading?: boolean
  errorMsg?: string
  done?: boolean
  echeance?: Date
  resourceType?: string
  sn?: string
}

const styleModal = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  border: '2px solid #000',
  boxShadow: 24,
  height: '60%',
  width: '60%',
  overflow: 'hidden',
}

const ModalCopyRequiredTools = ({ open, onClose, resources, setSelectedResource, lang, oenumdoc }: Props) => {
  const { t } = useTranslation()

  const checkedRequiredTools = Object.entries(resources).flatMap(([resourceType, requiredTools]) =>
    requiredTools.filter((r) => r.checked),
  )

  const [copyStateByPn, setCopyStateByPn] = useState<Record<string, CopyState>>({})
  const [isLoading, setIsLoading] = useState(false)

  const updateCopyState = (selected: RequiredTool, newState: CopyState) =>
    setCopyStateByPn((prev) => ({
      ...prev,
      [selected.pn]: { ...prev[selected.pn], ...newState },
    }))

  const handleSnSelect = (selected: RequiredTool) => {
    updateCopyState(selected, { sn: selected.sn, open: false })
  }

  const handleModalOpen = (pn: string) => {
    updateCopyState({ pn }, { open: true })
  }

  const handleModalClose = () => checkedRequiredTools.map((s) => updateCopyState(s, { open: true }))

  const submitSelectedRequiredTool = async (selected: RequiredTool, headerCompleted: any) => {
    try {
      const copyState = copyStateByPn[selected.pn]
      if (
        [PN_TYPES.CONSOMMABLE_BATCH_NUMBER, PN_TYPES.SERIALISE_REPARABLE, PN_TYPES.SERIALISE_NON_REPARABLE].includes(
          selected.equipmentType,
        ) &&
        !copyState.sn
      ) {
        //  && selected.oncodte && ['5', '6'].includes(selected.oncodte)
        updateCopyState(selected, { errorMsg: t('required_sn_field') })
        return
      }

      if (copyState.done) return

      updateCopyState(selected, { loading: true, errorMsg: undefined })

      const ounumdoc1 = oenumdoc?.substring(0, 4)
      const ounumdoc2 = oenumdoc?.substring(4)
      const requestData = {
        ounumdoc1,
        ounumdoc2,
        echeance: copyState.echeance && format(copyState.echeance, 'P'),
        codeType: selected.codeType,
        sn: selected.sn && selected.sn.toUpperCase(),
        pn: selected.pn && selected.pn.toUpperCase(),
      }
      setIsLoading(true)

      await axios.post(encodeURI(urlApis.URL_WORKORDER_USED_TOOLS_POST), requestData, {
        headers: headerCompleted,
        withCredentials: false,
        params: { lang },
      })
      setIsLoading(false)

      setSelectedResource(selected as RequiredToolInput)
      updateCopyState(selected, { loading: false, done: true, errorMsg: undefined })
    } catch (e) {
      console.error(e)
      if (e instanceof AxiosError) {
        if (e.response?.data.messages?.length > 0) {
          updateCopyState(selected, { loading: false, errorMsg: e.response?.data.messages[0].description })
        } else {
          updateCopyState(selected, { loading: false, errorMsg: e.message })
        }
      }
    }
  }

  const handleSubmit = async () => {
    setIsLoading(true)

    const headerCompleted = await axiosUtils.getHeader(axiosUtils.HEADERS.HEADER_CREATE)

    setIsLoading(false)

    checkedRequiredTools.forEach((s) => submitSelectedRequiredTool(s, headerCompleted))
  }

  const handleDateChange = (echeance: Date, selected: RequiredTool) => {
    updateCopyState(selected, { echeance })
  }

  const handleSNChange = (selected: RequiredTool, val: string) => {
    updateCopyState(selected, { sn: val })
  }
  const { getBackgroundColor } = useContext(LightDarkContext)

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <div style={{ ...styleModal, backgroundColor: getBackgroundColor() }}>
          <PageHeader
            pageTitle={t('modal_required_tools')}
            modalEmbedded={true}
            modalOnClose={onClose}
            style={{ top: 0 }}
            localLoading={isLoading}
          />
          <Container style={{ flex: 1 }} fixed>
            <div style={{ padding: 2, height: '78%' }}>
              <Table stickyHeader>
                <TableHead style={{ zIndex: 100 }}>
                  <TableRow>
                    <ResponsiveTableCell>{t('material_type')}</ResponsiveTableCell>
                    <ResponsiveTableCell>{t('9500047')}</ResponsiveTableCell>
                    <ResponsiveTableCell>{t('9500048')}</ResponsiveTableCell>
                    <ResponsiveTableCell>{t('7000248')}</ResponsiveTableCell>
                    <ResponsiveTableCell>{t('due_date')}</ResponsiveTableCell>
                    <ResponsiveTableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {checkedRequiredTools.map((requiredTool) => {
                    const copyState = copyStateByPn[requiredTool.pn]
                    return (
                      <TableRow key={requiredTool.pn} hover>
                        <ResponsiveTableCell>{requiredTool.libType}</ResponsiveTableCell>
                        <ResponsiveTableCell>{requiredTool.pn}</ResponsiveTableCell>
                        <ResponsiveTableCell>
                          {[
                            PN_TYPES.CONSOMMABLE_BATCH_NUMBER,
                            PN_TYPES.SERIALISE_REPARABLE,
                            PN_TYPES.SERIALISE_NON_REPARABLE,
                          ].includes(requiredTool.equipmentType) && (
                            <Inline spacing='half-x'>
                              <IconButton
                                onClick={() => handleModalOpen(requiredTool.pn)}
                                disabled={copyState?.done && !copyState?.errorMsg}
                                size='small'
                              >
                                <FontAwesomeIcon icon={faExternalLinkAlt} fontSize='inherit' size='xs' />
                              </IconButton>
                              <CustomInput
                                value={copyState.sn}
                                required
                                onChange={(val) => handleSNChange(requiredTool, val as string)}
                                disabled={copyState?.done && !copyState?.errorMsg}
                                capitalize
                                maxLength={15}
                              />
                            </Inline>
                          )}
                        </ResponsiveTableCell>
                        <ResponsiveTableCell>{requiredTool.designation}</ResponsiveTableCell>
                        <ResponsiveTableCell>
                          <DatePicker
                            value={copyState?.echeance}
                            onChange={(date) => date && handleDateChange(date, requiredTool)}
                            disabled={copyState?.done && !copyState?.errorMsg}
                          />
                        </ResponsiveTableCell>
                        <ResponsiveTableCell width='50px'>
                          {copyState?.loading && <Spinner size='small' />}
                          {!!copyState?.errorMsg && (
                            <IconButton variant='red'>
                              <FontAwesomeIcon icon={faCircleExclamation} title={copyState?.errorMsg} />
                            </IconButton>
                          )}
                          {copyState?.done && !copyState?.errorMsg && (
                            <IconButton variant='success'>
                              <FontAwesomeIcon icon={faSquareCheck} />
                            </IconButton>
                          )}
                        </ResponsiveTableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
            <Inline
              align='space-around'
              style={{
                padding: '10px',
                position: 'absolute',
                bottom: 0,
                marginTop: '1em',
                width: '100%',
              }}
            >
              <Button onClick={onClose} variant='secondary'>
                {t('8000569')}
              </Button>
              <Button onClick={handleSubmit} variant='primary'>
                {t('copy_used_resources')}
              </Button>
            </Inline>
          </Container>
        </div>
      </Modal>
      <ModalSn
        formTitle={t('9500144')}
        isOpened={Object.values(copyStateByPn)?.some((s) => s.open)}
        onCloseModal={handleModalClose}
        pn={Object.entries(copyStateByPn).find(([pn, copyState]) => copyState.open)?.[0] || ''}
        onSnSelected={handleSnSelect}
      />
    </>
  )
}

export default ModalCopyRequiredTools
